import { useEffect, useRef } from 'react'
import * as maptilersdk from '@maptiler/sdk'
import { decodeElevationGrid, tileCenterLat, slopeStats } from './terrain-utils'
import { elevCache, cacheSet } from './terrain-cache'
import { computeTileInWorker } from './terrain-worker-pool'
import { viewshedStore } from '../viewshed/viewshed-store'

const DIAG_TILE = { z: 15, x: 7830, y: 12852 } // lat 36.13015, lon -93.97176 at z=15

const SRC_ID   = 'terrain-analysis-src'
const LAYER_ID = 'terrain-analysis-layer'
const PROTOCOL = 'terrain-analysis'

function removeLayers(map) {
  try { if (map.getLayer(LAYER_ID)) map.removeLayer(LAYER_ID) } catch (_) {}
  try { if (map.getSource(SRC_ID)) map.removeSource(SRC_ID) } catch (_) {}
}

export default function TerrainOverlay({ map, filters, applyTrigger, enabled }) {
  const filterRef = useRef(filters)
  const appliedRef = useRef(false)
  const tileUrlRef = useRef(null)

  useEffect(() => { filterRef.current = filters }, [filters])

  // Toggle visibility without recomputing tiles
  useEffect(() => {
    if (!map || !appliedRef.current) return
    try {
      if (map.getLayer(LAYER_ID)) {
        map.setLayoutProperty(LAYER_ID, 'visibility', enabled ? 'visible' : 'none')
      }
    } catch (_) {}
  }, [enabled, map])

  // Register protocol once per map instance. The handler always reads filterRef.current
  // so it picks up new filters without re-registration.
  useEffect(() => {
    if (!map) return
    try { maptilersdk.removeProtocol(PROTOCOL) } catch (_) {}

    maptilersdk.addProtocol(PROTOCOL, async (params, abortController) => {
      const parts = params.url.replace(`${PROTOCOL}://`, '').split('/')
      const z = parseInt(parts[0], 10)
      const x = parseInt(parts[1], 10)
      const y = parseInt(parts[2], 10) // parseInt stops at '?', safely ignores ?v= suffix

      const cacheKey = `${z}/${x}/${y}`
      let elev = elevCache.get(cacheKey)

      if (!elev) {
        const tileUrl = `https://elevation-tiles-prod.s3.amazonaws.com/terrarium/${z}/${x}/${y}.png`
        const res = await fetch(tileUrl, { signal: abortController.signal })
        if (!res.ok) throw new Error(`terrarium tile ${res.status}`)

        const blob = await res.blob()
        // colorSpaceConversion: 'none' prevents sRGB gamma from corrupting raw RGB elevation data
        let bitmap
        try {
          bitmap = await createImageBitmap(blob, { colorSpaceConversion: 'none' })
        } catch (_) {
          bitmap = await createImageBitmap(blob)
        }

        const inCanvas = new OffscreenCanvas(256, 256)
        const inCtx = inCanvas.getContext('2d', { willReadFrequently: true })
        inCtx.drawImage(bitmap, 0, 0)
        elev = decodeElevationGrid(inCtx.getImageData(0, 0, 256, 256).data)
        cacheSet(cacheKey, elev)
      }

      const tileLat = tileCenterLat(y, z)

      if (z === DIAG_TILE.z && x === DIAG_TILE.x && y === DIAG_TILE.y) {
        console.log('[terrain-diag] tile', `${z}/${x}/${y}`, slopeStats(elev, z, tileLat))
      }

      // Offload the per-pixel compute + PNG encode to a worker so re-applies
      // (filter changes, viewshed updates) don't block map interaction.
      const data = await computeTileInWorker(
        elev, z, x, y, filterRef.current, viewshedStore.data, abortController.signal,
      )
      return { data }
    })

    return () => {
      removeLayers(map)
      appliedRef.current = false
      try { maptilersdk.removeProtocol(PROTOCOL) } catch (_) {}
    }
  }, [map])

  // Re-add source+layer after a basemap style change (setStyle wipes all custom layers).
  // Protocol and elevation cache survive setStyle, so tiles re-render from cache.
  useEffect(() => {
    if (!map) return
    const onStyleLoad = () => {
      if (!appliedRef.current || !tileUrlRef.current) return
      try {
        map.addSource(SRC_ID, {
          type: 'raster',
          tiles: [tileUrlRef.current],
          tileSize: 256,
          minzoom: 5,
          maxzoom: 15,
        })
        map.addLayer({
          id: LAYER_ID,
          type: 'raster',
          source: SRC_ID,
          paint: {
            'raster-opacity': filterRef.current.opacity / 100,
            'raster-fade-duration': 0,
          },
          layout: { visibility: filterRef.current.enabled ? 'visible' : 'none' },
        })
      } catch (e) {
        console.error('TerrainOverlay: failed to re-add layer after style change', e)
      }
    }
    map.on('style.load', onStyleLoad)
    return () => map.off('style.load', onStyleLoad)
  }, [map])

  // Add source+layer on first Apply; update tile URL on subsequent applies.
  // The ?v= suffix busts the map's internal tile cache without removing the layer.
  useEffect(() => {
    if (!map || applyTrigger === 0) return

    const tileUrl = `${PROTOCOL}://{z}/{x}/{y}?v=${applyTrigger}`
    tileUrlRef.current = tileUrl

    if (!appliedRef.current) {
      appliedRef.current = true
      try {
        map.addSource(SRC_ID, {
          type: 'raster',
          tiles: [tileUrl],
          tileSize: 256,
          minzoom: 5,
          maxzoom: 15,
        })
        map.addLayer({
          id: LAYER_ID,
          type: 'raster',
          source: SRC_ID,
          paint: {
            'raster-opacity': filters.opacity / 100,
            'raster-fade-duration': 0,
          },
          layout: { visibility: filters.enabled ? 'visible' : 'none' },
        })
      } catch (e) {
        console.error('TerrainOverlay: failed to add layer', e)
      }
    } else {
      try {
        const src = map.getSource(SRC_ID)
        if (src?.setTiles) {
          src.setTiles([tileUrl])
        } else if (src) {
          // Fallback for older SDK versions without setTiles
          removeLayers(map)
          map.addSource(SRC_ID, {
            type: 'raster',
            tiles: [tileUrl],
            tileSize: 256,
            minzoom: 5,
            maxzoom: 15,
          })
          map.addLayer({
            id: LAYER_ID,
            type: 'raster',
            source: SRC_ID,
            paint: {
              'raster-opacity': filters.opacity / 100,
              'raster-fade-duration': 0,
            },
            layout: { visibility: filters.enabled ? 'visible' : 'none' },
          })
        }
      } catch (e) {
        console.error('TerrainOverlay: failed to update tiles', e)
      }
    }
  }, [applyTrigger]) // eslint-disable-line react-hooks/exhaustive-deps

  // Re-apply opacity without recomputing tiles
  useEffect(() => {
    if (!map || !appliedRef.current) return
    try {
      if (map.getLayer(LAYER_ID)) {
        map.setPaintProperty(LAYER_ID, 'raster-opacity', filters.opacity / 100)
      }
    } catch (_) {}
  }, [filters.opacity, map])

  return null
}
